import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";
import { formatPrice, formatSize, SIZE_EU } from "@/lib/api";
import { toast } from "sonner";

export default function QuickView({ product, open, onOpenChange }) {
    const { addItem } = useCart();
    const { t, lang } = useLanguage();
    const [size, setSize] = useState(null);
    const [activeImage, setActiveImage] = useState(0);

    const name = lang === "en" && product.name_en ? product.name_en : product.name;
    const description = lang === "en" && product.description_en ? product.description_en : product.description;

    const images = product.images?.length ? product.images : [product.image];
    const singleSize = product.sizes.length === 1;

    useEffect(() => {
        if (open) {
            setSize(singleSize ? product.sizes[0] : null);
            setActiveImage(0);
        }
    }, [open, singleSize, product.sizes]);

    const handleAdd = () => {
        if (!size) {
            toast.error(t("quickview.selectSize"));
            return;
        }
        addItem({ ...product, name }, size);
        onOpenChange(false);
        toast.success(`${name} ${t("quickview.added")}`);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-3xl p-0 bg-white border-none overflow-hidden"
                data-testid="quick-view-dialog"
            >
                <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="relative bg-zinc-100 aspect-[3/4] md:aspect-auto md:min-h-[520px]">
                        <img
                            src={images[activeImage]}
                            alt={product.name}
                            className="absolute inset-0 w-full h-full object-cover object-top"
                            data-testid="quick-view-image"
                        />
                        {images.length > 1 && (
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                                {images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveImage(i)}
                                        aria-label={`Foto ${i + 1}`}
                                        data-testid={`quick-view-thumb-${i}`}
                                        className={`w-12 h-14 overflow-hidden border-2 transition-colors duration-200 bg-white ${
                                            activeImage === i ? "border-black" : "border-transparent"
                                        }`}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="p-8 md:p-10 flex flex-col justify-between">
                        <div>
                            <DialogTitle asChild>
                                <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight" data-testid="quick-view-title">
                                    {name}
                                </h3>
                            </DialogTitle>
                            <p className="font-display text-xl font-bold mt-3" data-testid="quick-view-price">
                                {formatPrice(product.price)}
                            </p>
                            <p className="text-sm text-zinc-600 leading-relaxed mt-5">
                                {description}
                            </p>

                            <div className="mt-8">
                                <p className="text-xs tracking-[0.25em] uppercase font-semibold mb-3">
                                    {t("quickview.size")} {singleSize && <span className="text-zinc-400 normal-case tracking-normal">— {t("quickview.onlySize").replace("{size}", formatSize(product.sizes[0]))}</span>}
                                </p>
                                <div className="flex gap-2">
                                    {product.sizes.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setSize(s)}
                                            data-testid={`size-${s}`}
                                            className={`w-14 h-14 border flex flex-col items-center justify-center transition-colors duration-200 ${
                                                size === s
                                                    ? "bg-black text-white border-black"
                                                    : "border-zinc-300 hover:border-black"
                                            }`}
                                        >
                                            <span className="text-sm font-semibold leading-none">{s}</span>
                                            {SIZE_EU[s] && (
                                                <span className={`text-[9px] mt-1 leading-none ${size === s ? "text-zinc-300" : "text-zinc-400"}`}>
                                                    EU {SIZE_EU[s]}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleAdd}
                            data-testid="quick-view-add-to-cart"
                            className="mt-10 w-full bg-black text-white py-4 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 hover:bg-zinc-800"
                        >
                            {t("quickview.add")}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
